export interface NavigateArgs {
  pathname?: string | null
  url?: string | null
}

/** Static list pages only — routes with {project_id} or {orgId} must use full pathnames from APP_STATE. */
export const PATHNAME_ALIASES: Record<string, string> = {
  // Home & projects
  home: '/home',
  homepage: '/home',
  'home page': '/home',
  projects: '/projects',
  'projects page': '/projects',
  'project list': '/projects',

  // Control & configuration
  control: '/control',
  'control tab': '/control',
  'control page': '/control',
  configuration: '/configuration',
  'user settings': '/configuration',

  // Core list pages
  contacts: '/contacts',
  calendar: '/calendar',
  cashflow: '/cashflow',
  transactions: '/transactions',
  events: '/events',
  activities: '/events',
  roles: '/roles',
  team: '/roles',
  wallet: '/wallet',
  connect: '/wallet_products',

  // Catalogues & templates
  shop: '/shop',
  'system templates': '/system_templates',
  'auto design templates': '/system_templates',
  'auto design template': '/system_templates',
  'proposal templates': '/proposal_templates',
  'proposal template': '/proposal_templates',
  contracts: '/contracts',
  'contract templates': '/contracts',
  testimonials: '/testimonials',
  'case studies': '/testimonials',

  // Pricing & payments
  'payment options': '/payment_options',
  'payment option': '/payment_options',
  'pricing schemes': '/pricing_schemes',
  'pricing scheme': '/pricing_schemes',
  'price schemes': '/pricing_schemes',
  costings: '/costings',
  'cost schemes': '/costings',
  commissions: '/commissions',
  adders: '/adders',
  'adders library': '/adders',

  // Components & design settings
  'utility tariffs': '/utility_tariffs',
  'utility tariff': '/utility_tariffs',
  'battery schemes': '/battery_schemes',
  'battery control schemes': '/battery_schemes',
  'project configurations': '/project_configurations',
  'setbacks and design settings': '/project_configurations',
  'design settings': '/project_configurations',
  modules: '/component_module_activations',
  inverters: '/component_inverter_activations',
  batteries: '/component_battery_activations',
  'other components': '/component_other_activations',

  // Org setup
  workflows: '/workflows',
  'project workflows': '/workflows',
  tags: '/tags',
  'project tags': '/tags',
  'custom fields': '/custom_fields',
  'custom forms': '/custom_forms',
  'document templates': '/document_templates',
  'lead capture': '/lead_capture_forms',
  'lead capture forms': '/lead_capture_forms',
  'private files': '/private_files',
  files: '/private_files',
  'public files': '/public_files',
  incentives: '/incentives',
}

export const NAVIGATE_TARGET_ERROR = 'navigate requires a valid pathname or url'

/** Reject protocol-relative paths, absolute URLs, and other non in-app routes. */
export const isSafeInAppPath = (path: string): boolean => {
  const trimmed = path.trim()
  if (!trimmed.startsWith('/') || trimmed.startsWith('//') || trimmed.includes('://')) {
    return false
  }
  return true
}

const normalizePathname = (pathname: string): string | null => {
  const trimmed = pathname.trim()
  if (!trimmed) {
    return null
  }
  const alias = PATHNAME_ALIASES[trimmed.toLowerCase()]
  const normalized = alias || (trimmed.startsWith('/') ? trimmed : `/${trimmed}`)
  return isSafeInAppPath(normalized) ? normalized : null
}

const resolveSameOriginUrlPath = (url: string): string | null => {
  try {
    const parsed = new URL(url, window.location.origin)
    if (parsed.origin !== window.location.origin) {
      return null
    }
    const path = `${parsed.pathname}${parsed.search}${parsed.hash}`
    return isSafeInAppPath(path) ? path : null
  } catch {
    return null
  }
}

export const resolveNavigateTarget = (args: NavigateArgs): string | null => {
  if (args.pathname && args.pathname !== 'null') {
    return normalizePathname(String(args.pathname))
  }
  if (args.url && args.url !== 'null') {
    const url = String(args.url).trim()
    if (url.startsWith('/')) {
      const path = url.split('#')[0]
      return isSafeInAppPath(path) ? path : null
    }
    if (url.startsWith('//') || url.includes('://')) {
      return resolveSameOriginUrlPath(url)
    }
    const fromAbsoluteOrRelative = resolveSameOriginUrlPath(url)
    if (fromAbsoluteOrRelative) {
      return fromAbsoluteOrRelative
    }
    const hashPath = url.split('#')[1]
    return hashPath ? normalizePathname(hashPath) : null
  }
  return null
}

export const resolveNavigateTargetOrThrow = (args: NavigateArgs): string => {
  const target = resolveNavigateTarget(args)
  if (!target) {
    throw new Error(NAVIGATE_TARGET_ERROR)
  }
  return target
}
